import { useAuiState } from "@assistant-ui/react";
import {
    createContext,
    type Dispatch,
    type MutableRefObject,
    type SetStateAction,
    useContext,
} from "react";

export class StaleThreadSwitchError extends Error {
    constructor(remoteId: string) {
        super(`Thread ${remoteId} is no longer the requested thread`);
        this.name = "StaleThreadSwitchError";
    }
}

export type RequestThread = (remoteId: string | null) => void;

export interface ThreadTargetValue {
    requestThread: RequestThread;
    target: string | null;
    loadingThreadId: string | null;
}

export const ThreadTargetContext = createContext<ThreadTargetValue | null>(
    null
);

const NO_REQUEST: RequestThread = () => {};

export function useRequestThread(): RequestThread {
    return useContext(ThreadTargetContext)?.requestThread ?? NO_REQUEST;
}

interface FenceThreadFetchDeps<TResult> {
    baseFetch: (remoteId: string) => Promise<TResult>;
    threadTargetRef: MutableRefObject<string | null>;
    setLoadingThreadId: Dispatch<SetStateAction<string | null>>;
}

/** Reject a fetch when another thread was requested while it loaded, so the runtime never switches to it. */
export function fenceThreadFetch<TResult>({
    baseFetch,
    threadTargetRef,
    setLoadingThreadId,
}: FenceThreadFetchDeps<TResult>): (remoteId: string) => Promise<TResult> {
    return async (remoteId: string) => {
        const targetAtStart = threadTargetRef.current;
        setLoadingThreadId(remoteId);
        try {
            const result = await baseFetch(remoteId);
            const target = threadTargetRef.current;
            if (target !== targetAtStart && target !== remoteId) {
                throw new StaleThreadSwitchError(remoteId);
            }
            return result;
        } finally {
            setLoadingThreadId((current) =>
                current === remoteId ? null : current
            );
        }
    };
}

/** Report whether the requested thread is still on its way to becoming the main thread. */
export function useThreadSwitchHold(): boolean {
    const ctx = useContext(ThreadTargetContext);
    const target = ctx?.target ?? null;
    const loadingThreadId = ctx?.loadingThreadId ?? null;
    return useAuiState((s) => {
        if (target === null) return false;
        const items = s.threads.threadItems;
        const main = items.find((item) => item.id === s.threads.mainThreadId);
        if (main?.remoteId === target) return false;
        return (
            loadingThreadId === target ||
            items.some((item) => item.remoteId === target)
        );
    });
}
